import { useRouter } from 'next/router'
import React, { ReactNode, useMemo } from 'react'
import { I18n } from 'react-polyglot'
import { QueryCache, ReactQueryCacheProvider } from 'react-query'
import { useIsomorphicLayoutEffect } from 'react-use'

import CssVariablesProvider from '~/providers/CssVariablesProvider'
import GlobalDataProvider from '~/providers/GlobalDataProvider'
import NavigationProvider from '~/providers/NavigationProvider'
import StyleProvider from '~/providers/StyleProvider'
import ThemeProvider from '~/providers/ThemeProvider'

import { DefaultPageData } from '~/data/page-data/serializer'

import gridPresets from '~/styles/grid-presets/styles.module.scss'
import ratioPresets from '~/styles/ratio-presets/styles.module.scss'
import textPresets from '~/styles/text-presets/styles.module.scss'
import backgroundColors from '~/styles/theme-classes/background-color.module.scss'
import colors from '~/styles/theme-classes/colors.module.scss'
import themePresets from '~/styles/theme-presets/styles.module.scss'

export interface AppProviderProps {
  children: ReactNode | ReactNode[]
  pageProps: DefaultPageData
  // global: GlobalProps
}

const stylePresets = {
  textPreset: textPresets,
  color: colors,
  grid: gridPresets,
  ratio: ratioPresets,
  backgroundColor: backgroundColors,
}

const queryCache = new QueryCache()

function AppProvider({ children, pageProps }: AppProviderProps) {
  const { locale } = useRouter()

  const memoizedGlobalProviderData = useMemo(
    () => ({
      ...pageProps?.globalData,
      global: pageProps?.global,
      redirects: pageProps?.global?.redirects,
    }),
    [pageProps?.globalData, pageProps?.global?.redirects],
  )

  return (
    <ReactQueryCacheProvider queryCache={queryCache}>
      <I18n
        locale={locale}
        messages={pageProps?.global?.dictionary}
        allowMissing={true}>
        <GlobalDataProvider value={memoizedGlobalProviderData}>
          <StyleProvider value={stylePresets}>
            <ThemeProvider value={themePresets}>
              <CssVariablesProvider
                value={{ 'scroll-bar': '0px', 'vh-100': '100vh' }}>
                <NavigationProvider value={{ open: false }}>
                  {children}
                </NavigationProvider>
              </CssVariablesProvider>
            </ThemeProvider>
          </StyleProvider>
        </GlobalDataProvider>
      </I18n>
    </ReactQueryCacheProvider>
  )
}

AppProvider.defaultProps = {}

export default AppProvider
